import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  HStack,
  VStack,
  Box,
  Text,
  Icon,
  Tooltip,
  Badge,
} from "@chakra-ui/react";
import { FiPhone, FiFileText, FiUsers } from "react-icons/fi";
import GarrazCopyText from "./GaraazCopyText";
import { formatMobileNumber, getNationalMobileForCopy } from "../../../utils/util";

interface ContactPerson {
  name: string;
  mobileNumber: string;
  note?: string;
}

interface ContactPersonsNestedPopoverProps {
  contactPersons: ContactPerson[];
}

const ContactPersonsNestedPopover: React.FC<
  ContactPersonsNestedPopoverProps
> = ({ contactPersons }) => {
  const hasContacts = contactPersons && contactPersons.length > 0;

  return (
    <Popover trigger="hover" placement="right-start" offset={[0, 8]} isLazy>
      <PopoverTrigger>
        <Badge
          colorScheme="blue"
          borderRadius="md"
          px={2}
          py={1}
          fontSize="xs"
          cursor="pointer"
          display="flex"
          alignItems="center"
          gap={1}
        >
          <Icon as={FiUsers} boxSize={3} />
          {contactPersons.length}
        </Badge>
      </PopoverTrigger>

      <PopoverContent
        width="250px"
        boxShadow="lg"
        borderColor="gray.200"
        _focus={{ outline: "none" }}
      >
        <PopoverHeader
          fontWeight="semibold"
          fontSize="sm"
          borderBottomWidth="1px"
          bg="gray.50"
        >
          Contact Persons ({contactPersons.length})
        </PopoverHeader>

        <PopoverBody maxH="350px" overflowY="auto" p={2}>
          {!hasContacts ? (
            <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
              No available contacts
            </Text>
          ) : (
            <VStack align="stretch" spacing={1.5}>
              {contactPersons.map((contact, index) => (
                <Box
                  key={index}
                  p={2}
                  borderWidth="1px"
                  borderRadius="md"
                  borderColor="gray.200"
                  bg="white"
                  _hover={{
                    borderColor: "blue.300",
                    boxShadow: "sm",
                  }}
                  transition="all 0.2s"
                >
                  {/* Name and Mobile Number */}
                  <HStack spacing={1} justify="space-between" align="center">
                    <VStack align="flex-start" spacing={0} flex={1} minW={0}>
                      <Tooltip
                        label={contact.name}
                        isDisabled={!contact.name || contact.name.length <= 20}
                      >
                        <Text
                          fontSize="sm"
                          fontWeight="semibold"
                          color="gray.800"
                          noOfLines={1}
                          maxWidth="140px"
                          overflow="hidden"
                          textOverflow="ellipsis"
                        >
                          {contact.name}
                        </Text>
                      </Tooltip>
                      <HStack spacing={0.5} mt={0}>
                        <Icon as={FiPhone} boxSize={3.5} color="blue.500" />
                        <Text fontSize="sm" color="gray.600">
                          {formatMobileNumber(contact.mobileNumber)}
                        </Text>
                        <GarrazCopyText
                          text={getNationalMobileForCopy(contact.mobileNumber)}
                          popOverMessage="Mobile Number Copied"
                        />
                      </HStack>
                    </VStack>
                  </HStack>

                  {/* Notes - truncated to 20 chars with tooltip */}
                  {contact.note && (
                    <Tooltip label={contact.note} placement="top" hasArrow>
                      <HStack mt={1} spacing={1} align="center">
                        <Icon
                          as={FiFileText}
                          boxSize={3.5}
                          color="gray.500"
                          flexShrink={0}
                        />
                        <Text fontSize="sm" color="gray.600" noOfLines={1}>
                          {contact.note.length > 20
                            ? `${contact.note.substring(0, 20)}...`
                            : contact.note}
                        </Text>
                      </HStack>
                    </Tooltip>
                  )}
                </Box>
              ))}
            </VStack>
          )}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default ContactPersonsNestedPopover;
